import { createSlice } from "@reduxjs/toolkit";
import { fetchData } from "./apiCallSlice";
import { selectApiUrls } from "./apiUrlSlice";
import { portalApi } from "../api/portalApi";
import { criticalDataArgs, criticalDataScope } from '../api/criticalDataContract';
import { refreshCriticalResource } from './criticalDataSlice';

const getHoverDescriptionsFromPayload = (payload) => {
  if (Array.isArray(payload?.HoverDescriptions)) {
    return payload.HoverDescriptions;
  }

  if (Array.isArray(payload?.hoverDescriptions)) {
    return payload.hoverDescriptions;
  }

  return [];
};

const isHoverDescriptionsRequest = (requestUrl = "") =>
  requestUrl.includes("hover-descriptions");

const fieldDescriptionSlice = createSlice({
  name: "fieldDescriptions",
  initialState: {
    data: [],
    status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
  },
  reducers: {
    replaceFieldDescriptions: (state, action) => {
      state.data = action.payload;
      state.status = 'succeeded';
      state.error = null;
    },
    clearFieldDescriptions: (state) => {
      state.data = [];
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(portalApi.endpoints.getCriticalData.matchPending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addMatcher(portalApi.endpoints.getCriticalData.matchFulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = getHoverDescriptionsFromPayload(action.payload);
        state.error = null;
      })
      .addMatcher(portalApi.endpoints.getCriticalData.matchRejected, (state, action) => {
        state.status = "failed";
        state.error = action.error?.message || "Failed to fetch critical data";
      })
      .addMatcher(portalApi.endpoints.getData.matchPending, (state, action) => {
        const requestUrl = action?.meta?.arg?.originalArgs?.url || "";
        if (isHoverDescriptionsRequest(requestUrl)) {
          state.status = "loading";
        }
      })
      .addMatcher(portalApi.endpoints.getData.matchFulfilled, (state, action) => {
        const requestUrl = action?.meta?.arg?.originalArgs?.url || "";
        if (isHoverDescriptionsRequest(requestUrl)) {
          state.status = "succeeded";
          state.data = action.payload;
        }
      })
      .addMatcher(portalApi.endpoints.getData.matchRejected, (state, action) => {
        const requestUrl = action?.meta?.arg?.originalArgs?.url || "";
        if (isHoverDescriptionsRequest(requestUrl)) {
          state.status = "failed";
          state.error = action.error?.message || "Failed to fetch hover descriptions";
        }
      });
  },
});

export const { clearFieldDescriptions, replaceFieldDescriptions } = fieldDescriptionSlice.actions;

export const selectFieldDescriptions = (state) => state.fieldDescriptions.data;
export const selectFieldDescriptionsStatus = (state) => state.fieldDescriptions.status;
export const selectFieldDescriptionsError = (state) => state.fieldDescriptions.error;

export const fetchFieldDescriptions =
  ({ forceRefresh = false, fallbackToLegacy = true } = {}) =>
  async (dispatch, getState) => {
    try {
      if (forceRefresh) return await dispatch(refreshCriticalResource('hoverDescriptions')).unwrap();
      const shared = getState().criticalData;
      if (shared?.scope === criticalDataScope(getState()) && Array.isArray(shared.data.hoverDescriptions)) {
        return shared.data.hoverDescriptions;
      }
      const criticalData = await dispatch(
        portalApi.endpoints.getCriticalData.initiate(criticalDataArgs(getState()), {
          forceRefetch: Boolean(forceRefresh),
          subscribe: false,
        })
      ).unwrap();

      const hoverDescriptions = getHoverDescriptionsFromPayload(criticalData);

      if (Array.isArray(hoverDescriptions)) {
        return hoverDescriptions;
      }

      if (!fallbackToLegacy) {
        return hoverDescriptions;
      }
    } catch (error) {
      if (!fallbackToLegacy) {
        throw error;
      }
    }

    const state = getState();
    const apiUrl = selectApiUrls(state).hoverDescriptions;
    return dispatch(fetchData({ url: apiUrl })).unwrap();
};

export default fieldDescriptionSlice.reducer;
